import Experience from "../../Experience";
import * as THREE from 'three'
import EventEmitter from "../../Utils/EventEmitter";
import gsap from "gsap";
export default class Axis extends EventEmitter {
    constructor(data, sphere) {
        super()
        this.data = data;
        this.name = this.data.name;
        this.direction = this.data.direction;
        this.taste = this.data.taste;

        this.currentValue = 0;

        this.maxValue = 3;

        this.experience = new Experience()
        this.scene = this.experience.scene

        this.sphere = sphere

        this.sphereCenter = this.sphere.center
        this.sphereCenter = this.sphereCenter.clone()
        this.sphereScale = this.sphere.mesh.scale.x

        this.axisMaterial = new THREE.ShaderMaterial({
            uniforms: {
                color: { value: new THREE.Color(0xffffff) },
                uAlpha: { value: 0.0 }
            },
            vertexShader: `
                void main() {
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 color;
                uniform float uAlpha;
       
                void main() {
                    float visibility = smoothstep(0.0, 0.2, uAlpha);
                    if (visibility < 0.001) {
                        discard;
                    }
                    gl_FragColor = vec4(color, visibility);
                }
            `,
            blending: THREE.AdditiveBlending
        })

        this.markerMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.0,
            depthTest: false,

        })

        this.umamiMaterial = new THREE.MeshPhysicalMaterial({
            color: 0xffffff,
            transparent: false,
            roughness: 0.2,
            blending: THREE.AdditiveBlending,

        })
        this.umamiMaterial.onBeforeCompile = (shader, renderer) => {
            shader.uniforms.uAlpha = { value: 0.0 };

            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <common>',
                `#include <common>
                uniform float uAlpha;`
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <opaque_fragment>',
                `#include <opaque_fragment>
                float visibility = smoothstep(0.0, 0.2, uAlpha);
                // if (visibility < 0.005) discard;
                gl_FragColor.a *= uAlpha;`
            );

            this.umamiMaterial.userData.shader = shader;

            gsap.to(this.umamiMaterial.userData.shader.uniforms.uAlpha, {
                duration: 1,
                value: 0.1,
                delay: 1,
            })
        }

        this.createAxisMesh()

    }
    setValue(value) {
        if (value > this.maxValue) {
            value = this.maxValue
            console.warn('Value is greater than max value, setting to max value')
        } else if (value < 0) {
            value = 0
            console.warn('Value is less than 0, setting to 0')
        }
        this.currentValue = value
        this.trigger('valueChanged', [this.currentValue])
    }

    show() {
        gsap.to(this.axisMaterial.uniforms.uAlpha, {
            duration: 1,
            value: 0.05,
            delay: 1,
        })
        gsap.to(this.markerMaterial, {
            duration: 10,
            opacity: 0.5,
            delay: 1,
        })

    }
    createAxisMesh() {
        if (this.direction.length() === 0) {
            this.createUmami()
            return
        }

        const direction = this.direction
        // Create main axis line
        const geometry = new THREE.BufferGeometry()
        const material = this.axisMaterial
        const isHorizontal = Math.abs(direction.x) === 1
        const outsideSphereOffset = isHorizontal ? -0.2 : -0.15

        const count = 55
        const totalAxisLength = (this.sphere.radius / 1.04 - 1) / 2 + 0.025 + outsideSphereOffset
        const markerSpan = (this.sphere.radius / 1.04 - 1) / 2
        const points = []

        for (let i = 0; i < count; i++) {
            const increment = i / count
            const point = new THREE.Vector3()
            point.copy(this.sphereCenter.clone().add(this.direction))
            point.add(direction.clone().multiplyScalar(increment * totalAxisLength))
            points.push(...point.toArray())
        }
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3))
        geometry.attributes.position.needsUpdate = true

        const axisMesh = new THREE.Line(geometry, material)
        axisMesh.position.copy(this.sphere.mesh.position.clone().negate().multiplyScalar(1 / this.sphereScale))
        axisMesh.scale.set(1 / this.sphereScale, 1 / this.sphereScale, 1 / this.sphereScale)
        this.sphere.mesh.add(axisMesh)

        // Add increment markers
        for (let i = 0; i < 3; i++) {
            const markerGeometry = new THREE.BufferGeometry()
            const markerLength = 0.25 // Length of the marker line

            const markerPosition = (i * markerSpan) / 2
            let markerCenter = this.sphereCenter.clone().add(this.direction)
                .add(direction.clone().multiplyScalar(markerPosition))

            let perpendicular = new THREE.Vector3()
            if (Math.abs(this.direction.y) === 1) {
                perpendicular.set(1, 0, 0) // For vertical axis
            } else if (Math.abs(this.direction.x) === 1) {
                perpendicular.set(0, 1, 0) // For horizontal axis
            } else {
                perpendicular.set(1, 0, 0) // For other cases
            }
            perpendicular.normalize()

            if (i === 2) {
                const markerPoints = [
                    markerCenter.clone().add(perpendicular.clone().multiplyScalar(markerLength / 2)),
                    markerCenter.clone().add(perpendicular.clone().multiplyScalar(-markerLength / 2))
                ];

                const numDensityLines = 15;
                const densitySpread = 0.05;

                for (let k = 0; k < numDensityLines; k++) {
                    const offset = (k / (numDensityLines - 1) - 0.5) * densitySpread;

                    const densityLineGeometry = new THREE.BufferGeometry();

                    const offsetPoints = [
                        markerPoints[0].clone().add(this.direction.clone().multiplyScalar(offset + outsideSphereOffset)),
                        markerPoints[1].clone().add(this.direction.clone().multiplyScalar(offset + outsideSphereOffset))
                    ];
                    const offsetPositions = [];
                    offsetPoints.forEach(point => {
                        offsetPositions.push(...point.toArray());
                    });

                    densityLineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(offsetPositions, 3));

                    const densityLine = new THREE.Line(densityLineGeometry, this.markerMaterial);
                    densityLine.position.copy(this.sphere.mesh.position.clone().negate().multiplyScalar(1 / this.sphereScale));
                    densityLine.position.z -= 0.001 * k
                    densityLine.scale.set(1 / this.sphereScale, 1 / this.sphereScale, 1 / this.sphereScale);
                    densityLine.renderOrder = 15;

                    this.sphere.mesh.add(densityLine);
                }
            } else {
                let offset = i == 0 ? 0.05 : 0
                if (i == 0) {
                    markerCenter = markerCenter.clone().add(this.direction.clone().multiplyScalar(offset))
                }
                const markerPoints = [
                    markerCenter.clone().add(perpendicular.clone().multiplyScalar(markerLength / 2)),
                    markerCenter.clone().add(perpendicular.clone().multiplyScalar(-markerLength / 2))
                ]

                const markerPositions = []
                markerPoints.forEach(point => {
                    markerPositions.push(...point.toArray())
                })

                markerGeometry.setAttribute('position', new THREE.Float32BufferAttribute(markerPositions, 3))
                const markerLine = new THREE.Line(markerGeometry, material)
                markerLine.position.copy(this.sphere.mesh.position.clone().negate().multiplyScalar(1 / this.sphereScale))
                markerLine.scale.set(1 / this.sphereScale, 1 / this.sphereScale, 1 / this.sphereScale)
                this.sphere.mesh.add(markerLine)
            }
        }
    }
    createUmami() {
        for (var i = 0; i < this.maxValue; i++) {
            const radius = (i + 1) / 3;

            const sphereGeometry = new THREE.SphereGeometry(radius, 32, 32)
            const sphereMaterial = this.umamiMaterial

            const sphereMesh = new THREE.Mesh(sphereGeometry, sphereMaterial)
            // sphereMesh.renderOrder = 0
            // this.sphere.mesh.renderOrder = 0
            sphereMesh.position.copy(this.sphereCenter.clone().multiplyScalar(1 / this.sphereScale)).sub(new THREE.Vector3(0, this.sphere.mesh.position.y * (1 / this.sphereScale), 0))

            sphereMesh.scale.set(1 / this.sphereScale, 1 / this.sphereScale, 1 / this.sphereScale)
            this.sphere.mesh.add(sphereMesh)
        }
    }
}
